/* eslint-disable no-unused-expressions */

import React, { useEffect, useState } from 'react';
import { RichText, Text } from '@sitecore-jss/sitecore-jss-react';
import Sticky from 'react-sticky-el';
import SingleInput from '../CarLoanCalculator/SingleInput';
import ScheduleModal from '../CarLoanCalculator/ScheduleModal';
import { useInView } from 'react-intersection-observer';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import { PMT, InputField, isNumber } from '../CarLoanCalculator/helpers';
import { withTranslation } from 'react-i18next';
import clsx from 'clsx';
import '../CarLoanCalculator/style.scss';
import './style.scss';
import BpRadio from './BpRadio';

const PersonalLoanCalculator = ({ fields = {}, t, params }) => {
  // main
  const { title, description, currency } = fields;
  // I need to borrow
  const {
    borrowLabel,
    borrowSubLabel,
    borrowMin,
    borrowMax,
    borrowMaxLength,
    borrowDefault,
    borrowPlaceholder,
    borrowStep,
    borrowAdornmentText,
  } = fields;
  // Salary
  const {
    salaryLabel,
    salarySubLabel,
    salaryMin,
    salaryMax,
    salaryMaxLength,
    salaryDefault,
    salaryPlaceholder,
    salaryStep,
    salaryAdornmentText,
  } = fields;
  // Pay it back
  const {
    payBackLabel,
    payBackSubLabel,
    tenureDefault,
    tenureMax,
    tenureMaxLength,
    tenureMin,
    tenurePlaceholder,
    tenureStep,
    tenureAdornmentText,
  } = fields;
  // Interest
  const {
    interestRateDefault,
    interestRateLabel,
    interestRateMax,
    interestRateMaxLength,
    interestRateMin,
    interestRatePlaceholder,
    interestRateSubLabel,
    interestRateStep,
    interestRateAdornmentText,
  } = fields;
  // National UAE
  const { uAENationalLabel, enableUAENational } = fields;
  // Ministry
  const { ministryofDefenseLabel, enableMinistryofDefense } = fields;
  // additional calculations
  const { ministryMultplies, defaultMultplies, ministryTenureMax, ministryUAETenureMax } = fields;
  // right-card
  const {
    monthlyRepaymentLabel,
    totalRepayableLabel,
    indicativeAmortisationScheduledButton,
    additionalNote,
    additionalText,
  } = fields;
  // schedule table fields
  const {
    amortisationScheduleLabel,
    amortisationScheduleText,
    paymentNoLabel,
    installmentAmountLabel,
    principalLabel,
    interestLabel,
    closingBalanceLabel,
    openingBalanceLabel,
    amortisationScheduleAdditionalText,
  } = fields;

  const salaryValueInitial = (hideSalarySlider?.value !== true) ? salaryDefault?.value : 0;
  const { hideInterestRateSlider, hideSalarySlider } = fields;
  // Inputs state
  const [borrowValue, setBorrowValue] = useState(borrowDefault?.value);
  const [salaryValue, setSalaryValue] = useState(salaryValueInitial);
  const [payBackValue, setPayBackValue] = useState(tenureDefault?.value);
  const [interestValue, setInterestValue] = useState(interestRateDefault?.value);

  // right-card states
  const [monthlyRepayment, setMonthlyRepayment] = useState(0);
  const [totalRepayment, setTotalRepayment] = useState(0);

  // Radios states
  const [uaeNational, setUaeNational] = useState(null);
  const [ministry, setMinistry] = useState(null);

  // Schedule popup
  const [showModal, setShowModal] = useState(false);
  const tableFields = {
    amortisationScheduleLabel,
    amortisationScheduleText,
    paymentNoLabel,
    installmentAmountLabel,
    principalLabel,
    interestLabel,
    closingBalanceLabel,
    openingBalanceLabel,
    amortisationScheduleAdditionalText,
  };

  // Main
  const shouldRenderTitle = !!title?.value;
  const shouldRenderDescription = !!description?.value;
  const shouldRenderCurrency = !!currency?.value;
  // National UAE
  const shouldRenderUAENationalLabel = !!uAENationalLabel?.value;
  const shouldRenderEnableUAENational = !!enableUAENational?.value && enableUAENational?.value;
  // Ministry
  const shouldRenderMinistryofDefenseLabel = !!ministryofDefenseLabel?.value;
  const shouldRenderEnableMinistryofDefense =
    !!enableMinistryofDefense?.value && enableMinistryofDefense?.value;
  // right-card
  const shouldRenderMonthlyRepaymentLabel = !!monthlyRepaymentLabel?.value;
  const shouldRenderTotalRepayableLabel = !!totalRepayableLabel?.value;
  const shouldRenderIndicativeAmortisationScheduledButton =
    !!indicativeAmortisationScheduledButton?.value;
  const shouldRenderAdditionalText = !!additionalText?.value;
  const shouldRenderAdditionalNote = !!additionalNote?.value;
  // max borrow state
  const [maxBorrow, setMaxBorrow] = useState(borrowMax);
  // max tenure state
  const [maxTenure, setMaxTenure] = useState(tenureMax);
  // Borrow props
  const borrowProps = new InputField(
    borrowLabel,
    borrowSubLabel,
    borrowDefault,
    borrowMin,
    maxBorrow,
    borrowMaxLength,
    borrowPlaceholder,
    borrowStep,
    borrowAdornmentText
  );
  // Salary props
  const salaryProps = new InputField(
    salaryLabel,
    salarySubLabel,
    salaryDefault,
    salaryMin,
    salaryMax,
    salaryMaxLength,
    salaryPlaceholder,
    salaryStep,
    salaryAdornmentText
  );
  // Pay it back props
  const PayItBackProps = new InputField(
    payBackLabel,
    payBackSubLabel,
    tenureDefault,
    tenureMin,
    maxTenure,
    tenureMaxLength,
    tenurePlaceholder,
    tenureStep,
    tenureAdornmentText
  );
  // Interest props
  const interestProps = new InputField(
    interestRateLabel,
    interestRateSubLabel,
    interestRateDefault,
    interestRateMin,
    interestRateMax,
    interestRateMaxLength,
    interestRatePlaceholder,
    interestRateStep,
    interestRateAdornmentText
  );

  useEffect(() => {
    if (!isNumber(borrowValue) || !isNumber(payBackValue) || !isNumber(interestValue)) {
      return;
    }
    caclulateResults();
  }, [borrowValue, payBackValue, interestValue]);

  function caclulateResults() {
    const monthlyRepaymentResult = PMT(interestValue / 100 / 12, payBackValue, -borrowValue);
    setMonthlyRepayment(monthlyRepaymentResult);
    setTotalRepayment(monthlyRepaymentResult * payBackValue);
  }

  useEffect(() => {
    if (hideSalarySlider) return;
    if (ministry === 'yesMinistry') {
      setMaxBorrow({
        value: salaryValue * ministryMultplies?.value,
      });
      if (uaeNational === 'yesUae') {
        setMaxTenure({
          value: ministryUAETenureMax?.value,
        });
      } else {
        setMaxTenure({
          value: ministryTenureMax?.value,
        });
      }
    } else {
      setMaxBorrow({
        value: salaryValue * defaultMultplies?.value,
      });
      setMaxTenure(tenureMax);
    }
  }, [uaeNational, ministry, salaryValue]);

  const [componentRef, inView] = useInView({
    /* Optional options */
    threshold: 0.5,
    triggerOnce: false,
  });

  const changeRadioCB = (e) => {
    const val = e.target.value;
    if (val === 'yesUae') {
      setUaeNational('yesUae');
    } else if (val === 'noUae') {
      setUaeNational('noUae');
    } else if (val === 'yesMinistry') {
      setMinistry('yesMinistry');
    } else if (val === 'noMinistry') {
      setMinistry('noMinistry');
    }
  };

  return (
    <div
      id="personalCalc"
      ref={componentRef}
      className={clsx('car-loan-calculator content', 'add-margins', params?.Styles)}
    >
      <ScheduleModal
        showModal={showModal}
        setShowModal={setShowModal}
        payBackValue={payBackValue}
        borrow={borrowValue}
        monthlyRepayment={monthlyRepayment}
        data={{ ...tableFields }}
        reducingRate={interestValue}
        personal
      />
      {(shouldRenderTitle || shouldRenderDescription) && (
        <div className="car-loan-heading">
          {shouldRenderTitle && (
            <h3>
              <Text field={title} />
            </h3>
          )}
          {shouldRenderDescription && (
            <RichText className="car-loan-heading_description" field={description} />
          )}
        </div>
      )}
      <div className="car-loan-calculator-content-container">
        <div className="content-container-left">
          {shouldRenderEnableUAENational && (
            <>
              {shouldRenderUAENationalLabel && (
                <div className="uae-label">
                  <Text field={uAENationalLabel} />
                </div>
              )}
              <RadioGroup
                className="uae-national-radios"
                defaultValue="female"
                aria-labelledby="demo-customized-radios"
                name="customized-radios"
                onChange={changeRadioCB}
              >
                <FormControlLabel value="yesUae" control={<BpRadio />} label={t('yes')} />
                <FormControlLabel value="noUae" control={<BpRadio />} label={t('no')} />
              </RadioGroup>
            </>
          )}
          {shouldRenderEnableMinistryofDefense && (
            <>
              {shouldRenderMinistryofDefenseLabel && (
                <div className="salary-label">
                  <Text field={ministryofDefenseLabel} />
                </div>
              )}
              <RadioGroup
                className="uae-national-radios"
                defaultValue="female"
                aria-labelledby="demo-customized-radios"
                name="customized-radios"
                onChange={changeRadioCB}
              >
                <FormControlLabel value="yesMinistry" control={<BpRadio />} label={t('yes')} />
                <FormControlLabel value="noMinistry" control={<BpRadio />} label={t('no')} />
              </RadioGroup>
            </>
          )}
          {hideSalarySlider?.value !== true && (
            <SingleInput t={t} {...salaryProps} value={salaryValue} setValue={setSalaryValue} />
          )}
          <SingleInput t={t} {...borrowProps} value={borrowValue} setValue={setBorrowValue} />
          <SingleInput
            t={t}
            {...PayItBackProps}
            numbersOnly
            value={payBackValue}
            setValue={setPayBackValue}
          />
          {hideInterestRateSlider?.value !== true && (
            <SingleInput
              t={t}
              {...interestProps}
              value={interestValue}
              setValue={setInterestValue}
            />
          )}
        </div>
        <div className={clsx('car-loan-mobile d-xl-none', inView && 'toggled')}>
          <div className="content-container-right">
            <div className="result-card">
              {shouldRenderMonthlyRepaymentLabel && (
                <>
                  <div className="monthly__repayment--label result-label">
                    <Text field={monthlyRepaymentLabel} />
                  </div>
                  <div className="monthly__repayment--value result-value">
                    {shouldRenderCurrency && (
                      <span>
                        <Text field={currency} />
                      </span>
                    )}
                    <div>
                      {typeof monthlyRepayment === 'number' &&
                        Math.round(monthlyRepayment).toLocaleString()}
                    </div>
                  </div>
                </>
              )}
              {shouldRenderTotalRepayableLabel && (
                <>
                  <div className="total__repayable--label result-label">
                    <Text field={totalRepayableLabel} />
                  </div>
                  <div className="total__repayable--value result-value">
                    {shouldRenderCurrency && (
                      <span>
                        <Text field={currency} />
                      </span>
                    )}
                    <div>
                      {typeof totalRepayment === 'number' &&
                        Math.round(totalRepayment).toLocaleString()}
                    </div>
                  </div>
                </>
              )}
              {shouldRenderAdditionalText && <RichText className="note" field={additionalText} />}
              {shouldRenderIndicativeAmortisationScheduledButton && (
                <button
                  onClick={() => setShowModal(true)}
                  className="button button-contained-secondary btn-schedule"
                >
                  {indicativeAmortisationScheduledButton.value}
                </button>
              )}
            </div>
          </div>
        </div>
        <Sticky
          className="car-loan-sticky d-none d-xl-block"
          bottomOffset={0}
          boundaryElement=".car-loan-calculator-content-container"
        >
          <div className="content-container-right">
            <div className="result-card">
              {shouldRenderMonthlyRepaymentLabel && (
                <>
                  <div className="monthly__repayment--label result-label">
                    <Text field={monthlyRepaymentLabel} />
                  </div>
                  <div className="monthly__repayment--value result-value">
                    {shouldRenderCurrency && (
                      <span>
                        <Text field={currency} />
                      </span>
                    )}
                    <div>
                      {typeof monthlyRepayment === 'number' &&
                        Math.round(monthlyRepayment).toLocaleString()}
                    </div>
                  </div>
                </>
              )}
              {shouldRenderTotalRepayableLabel && (
                <>
                  <div className="total__repayable--label result-label">
                    <Text field={totalRepayableLabel} />
                  </div>
                  <div className="total__repayable--value result-value">
                    {shouldRenderCurrency && (
                      <span>
                        <Text field={currency} />
                      </span>
                    )}
                    <div>
                      {typeof totalRepayment === 'number' &&
                        Math.round(totalRepayment).toLocaleString()}
                    </div>
                  </div>
                </>
              )}
              {shouldRenderAdditionalNote && <RichText className="note" field={additionalNote} />}
              {shouldRenderIndicativeAmortisationScheduledButton && (
                <button
                  onClick={() => setShowModal(true)}
                  className="button button-contained-secondary btn-schedule"
                >
                  {indicativeAmortisationScheduledButton.value}
                </button>
              )}
            </div>
            {shouldRenderAdditionalText && (
              <RichText field={additionalText} className="additional-note" />
            )}
          </div>
        </Sticky>
      </div>
    </div>
  );
};

export default withTranslation()(PersonalLoanCalculator);
